export type SportsApiEnvironment = 'test' | 'live';

export enum League {
  NPC = 'npc',
  NRL = 'nrl',
  NZNBL = 'nznbl',
  WBC = 'wbc',
  NBL = 'nbl',
  TST = 'tst',
  SRP = 'srp',
  ALM = 'alm',
  RUIFG = 'ruifg',
  EPL = 'epl',
}

export type LeagueConfig = {
  id: League;
  name: string;
  icon: string;
  country: string;
  active: boolean;
  /**
   * Other names this league answers to.
   *
   * A league acquired two identities: the enum value here, and whatever
   * `comp.league` happens to hold. Internationals are `ruifg` in this file and
   * `intl` on every competition and team — and `ruifg` appears on no
   * competition at all. That mismatch made `getFeedExpectations` return nothing
   * for internationals, so the health sweep skipped them, and split their feed
   * health records across two league keys so the dashboard showed half of them.
   *
   * Resolve through `resolveLeague` rather than comparing ids by hand.
   */
  aliases?: readonly string[];

  /** Post-match results are written to the knowledge base. */
  postGameKbWrite?: boolean;
  /** A scheduled job keeps this league's knowledge current. */
  cronSync?: boolean;
  /** The league has a knowledge base at all. */
  knowledgeBase?: boolean;
  /**
   * The directory this league's knowledge files live under, when it differs
   * from the value competitions carry in `comp.league`.
   *
   * Knowledge is loaded by string prefix — `knowledge/{comp.league}/...` — so
   * the directory name and `comp.league` have to agree. They agree by accident
   * for every league except Tauihi, whose competitions carry `wbc` while the
   * files sit under `tauihi/`; the listing returned nothing and the prompt ran
   * with no club context at all, silently.
   *
   * Deliberately keyed off `comp.league` rather than resolved through
   * `resolveLeague`, because that would map the internationals' `intl` onto
   * their config id `ruifg` and break a case that currently works.
   */
  knowledgeId?: string;
  /**
   * Article tags that place a story in this league.
   *
   * Used to keep one competition's coverage out of another's questions: a
   * Ranfurly Shield report and a Springboks Test shared the tag
   * "new zealand rugby", which was enough to caption one with the other.
   */
  articleTags?: readonly string[];
} & (
  | {
      provider: 'genius';
      competitionId: string;
      sport: 'basketball';
      /**
       * Where the Genius data is read from.
       *
       * `api` (the default) uses the keyed `api.wh.geniussports.com` endpoints.
       * `dcd` reads the same competition from the public DCD pages and the FIBA
       * LiveStats feed, which need no key. See docs/tauihi-data-access.md.
       */
      dataSource?: 'api' | 'dcd';
    }
  | {
      provider: 'radar';
      seasonId: string;
      sport: 'rugby-union' | 'rugby-league' | 'basketball';
      apiVersion: 'v2' | 'v3';
    }
  | {
      provider: 'sofascore';
      uniqueTournamentId: string;
      seasonId: string;
      sport: 'rugby' | 'football' | 'basketball';
      apiVersion: 'v1';
      eventsPathGroup: string;
      eventsPathLeague: string;
    }
  | {
      provider: 'tabnz';
      category: string;
      regionSlug?: string;
      competitionSlug: string;
      sha256Hash: string;
      eventSha256Hash: string;
      sport: string;
    }
  | {
      provider: 'world_rugby';
      competitionSlug: string;
      sport: 'rugby-union';
      tabCategory?: string;
      tabCompetitionSlug?: string;
      tabSha256Hash?: string;
      tabEventSha256Hash?: string;
    }
  | {
      provider: 'rugbypass';
      competitionSlug: string;
      sport: 'rugby-union';
      tabCategory?: string;
      tabCompetitionSlug?: string;
      tabSha256Hash?: string;
      tabEventSha256Hash?: string;
    }
  | {
      provider: 'nrl';
      competition: string;
      sport: 'rugby-league';
      tabCategory?: string;
      tabCompetitionSlug?: string;
      tabSha256Hash?: string;
      tabEventSha256Hash?: string;
    }
  | {
      provider: 'sportradar_eui';
      embedId: string;
      entityId: string;
      competitionId: string;
      sport: 'basketball';
      /**
       * EUI season ID for the current season.
       *
       * The `fixtures?entityId=` endpoint only returns the previous season once
       * a new one starts. Passing this alongside entityId switches it to the
       * current season's schedule. Update each season.
       */
      seasonId?: string;
      /** Fixture IDs to always surface in the admin fixture picker (e.g. pre-season games not returned by the entity endpoint). */
      pinnedFixtureIds?: string[];
      tabCategory?: string;
      tabCompetitionSlug?: string;
      tabSha256Hash?: string;
    }
);

export const getLeagueConfiguration = (
  league: League,
): LeagueConfig | undefined =>
  LEAGUE_CONFIGURATIONS.find((config) => config.id === league);

const normaliseLeagueKey = (value: string): string =>
  value.trim().toLowerCase();

/**
 * Find a league by its id or any name it answers to.
 *
 * The single place that knows a league can be called more than one thing. Every
 * lookup keyed on a league should come through here rather than comparing ids,
 * because the caller usually has whatever was stored on a competition and that
 * is not always the enum value.
 *
 * Returns undefined for values with no league behind them — competitions carry
 * groupings like `1news`, `rwc` and `awards` in the same field, and those are
 * not leagues.
 */
export const resolveLeague = (
  value: string | undefined,
): LeagueConfig | undefined => {
  if (!value) {
    return undefined;
  }
  const wanted = normaliseLeagueKey(value);
  return LEAGUE_CONFIGURATIONS.find(
    (config) =>
      normaliseLeagueKey(config.id) === wanted ||
      config.aliases?.some((a) => normaliseLeagueKey(a) === wanted),
  );
};

/**
 * The provider's own slug for this league's competition.
 *
 * Only some provider variants carry one, so narrowing it here keeps the `in`
 * check out of every caller — and keeps anyone from writing a second lookup
 * table when the field is already on the league.
 */
/**
 * The knowledge directory for a competition's league.
 *
 * Takes the raw `comp.league` string and returns the directory its knowledge
 * files actually live under. Falls through to the input for every league whose
 * directory already matches, so existing behaviour is unchanged.
 */
export const knowledgeLeagueId = (league: string | undefined): string => {
  if (!league) return '';
  const wanted = normaliseLeagueKey(league);
  const config = LEAGUE_CONFIGURATIONS.find(
    (c) =>
      normaliseLeagueKey(c.id) === wanted ||
      c.aliases?.some((a) => normaliseLeagueKey(a) === wanted),
  );
  return config?.knowledgeId ?? league;
};

export const leagueCompetitionSlug = (
  value: string | undefined,
): string | undefined => {
  const config = resolveLeague(value);
  return config && 'competitionSlug' in config
    ? config.competitionSlug
    : undefined;
};

/** The canonical id for a league named by an id or alias. */
export const canonicalLeagueId = (
  value: string | undefined,
): string | undefined => resolveLeague(value)?.id ?? value;

// Use the following links to get the radar seasonId for the league:
// Get the competition id
// https://api.sportradar.com/basketball/trial/v2/en/competitions.json?api_key={apikey}
// Get the seasons
// https://api.sportradar.com/basketball/trial/v2/en/competitions/{competitionId}/seasons.json?api_key={apiKey}

/**
 * TAB NZ GraphQL persisted-query hashes. TAB rotates these when it redeploys its
 * web app, after which every TAB call returns PersistedQueryNotFound and odds
 * silently stop arriving on all leagues. To refresh: open tab.co.nz, fetch the
 * `vendor-graphql-ops-web-*.js` bundle it loads, and copy the values for
 * `SportingEvents` (fixture list) and `SportingEventScreen`.
 * Last refreshed 2026-10-07.
 */
export const TAB_EVENTS_HASH =
  'dc1a46de286e4a2b5cddfc25a250ae76f1a5b4eb8e28d293ecd3d88bfd294d42';
export const TAB_EVENT_SCREEN_HASH =
  '4001b3a3b8a9c424f146c3b657303a65ebb055d5f025037356b7083825a30ceb';

export const LEAGUE_CONFIGURATIONS: LeagueConfig[] = [
  // {
  //   id: League.NPC,
  //   name: 'National Provincial Championship',
  //   icon: 'https://www.provincial.rugby/assets/Uploads/Bunnings-Warehouse-NPC__ScaleHeightWzEyMF0.png',
  //   country: 'New Zealand',
  //   active: true,
  //   provider: 'radar',
  //   seasonId: 'sr:season:119461',
  //   sport: 'rugby-union',
  //   apiVersion: 'v3',
  // },
  {
    id: League.NPC,
    name: 'NPC',
    icon: 'https://www.provincial.rugby/assets/Uploads/Bunnings-Warehouse-NPC__ScaleHeightWzEyMF0.png',
    country: 'New Zealand',
    active: true,
    postGameKbWrite: true,
    cronSync: true,
    knowledgeBase: true,
    articleTags: [
      'npc',
      'provincial rugby',
      'ranfurly shield',
      'bunnings npc',
    ],
    provider: 'rugbypass',
    competitionSlug: 'bunnings-npc',
    sport: 'rugby-union',
    tabCategory: 'RUGBY_UNION',
    tabCompetitionSlug: 'new-zealand-npc',
    tabSha256Hash: TAB_EVENTS_HASH,
    tabEventSha256Hash: TAB_EVENT_SCREEN_HASH,
  },
  // {
  //   Older radar config
  //   id: League.NRL,
  //   name: 'National Rugby League',
  //   icon: 'https://www.nrl.com/.theme/nrl/badge.svg',
  //   country: 'Australia',
  //   active: true,
  //   provider: 'radar',
  //   seasonId: 'sr:season:111482',
  //   sport: 'rugby-league',
  //   apiVersion: 'v3',
  // },
  // {
  //   Older sofascore config
  //   id: League.NRL,
  //   name: 'National Rugby League',
  //   icon: 'https://www.nrl.com/.theme/nrl/badge.svg',
  //   country: 'Australia',
  //   active: true,
  //   provider: 'sofascore',
  //   uniqueTournamentId: '294',
  //   seasonId: '86317',
  //   sport: 'rugby',
  //   apiVersion: 'v1',
  //   eventsPathGroup: 'rugby-league',
  //   eventsPathLeague: 'nrl',
  // },
  {
    id: League.NRL,
    name: 'National Rugby League',
    icon: 'https://www.nrl.com/.theme/nrl/badge.svg',
    country: 'Australia',
    active: true,
    postGameKbWrite: true,
    cronSync: true,
    knowledgeBase: true,
    articleTags: ['nrl', 'rugby league', 'nrlw'],
    provider: 'nrl',
    competition: '111',
    sport: 'rugby-league',
    tabCategory: 'RUGBY_LEAGUE',
    tabCompetitionSlug: 'nrl',
    tabSha256Hash: TAB_EVENTS_HASH,
    tabEventSha256Hash: TAB_EVENT_SCREEN_HASH,
  },
  // {
  //   id: League.SRP,
  //   name: 'Super Rugby Pacific',
  //   icon: 'https://img.sofascore.com/api/v1/unique-tournament/422/image/dark',
  //   country: 'Australia',
  //   active: true,
  //   provider: 'sofascore',
  //   uniqueTournamentId: '422',
  //   seasonId: '86502',
  //   sport: 'rugby',
  //   apiVersion: 'v1',
  //   eventsPathGroup: 'rugby-union',
  //   eventsPathLeague: 'super-rugby',
  // },
  {
    id: League.SRP,
    name: 'Super Rugby Pacific',
    icon: 'https://img.sofascore.com/api/v1/unique-tournament/422/image/dark',
    country: 'Australia',
    active: true,
    provider: 'tabnz',
    category: 'RUGBY_UNION',
    competitionSlug: 'super-rugby-pacific',
    sha256Hash: TAB_EVENTS_HASH,
    eventSha256Hash: TAB_EVENT_SCREEN_HASH,
    sport: 'rugby-union',
  },
  {
    id: League.NBL,
    name: 'Australian National Basketball League',
    icon: 'https://img.sofascore.com/api/v1/unique-tournament/1524/image',
    country: 'Australia',
    active: true,
    knowledgeBase: true,
    articleTags: ['nbl', 'breakers', 'nz breakers', 'australian basketball'],
    provider: 'sportradar_eui',
    embedId: '298',
    entityId: 'bedd705b-410c-11f0-8d69-1b3c55488bbb',
    competitionId: '7e57ea66-410d-11f0-a509-25e02ef71af2',
    sport: 'basketball',
    /** NBL27 (2026-27 season). Update each season — switching seasons requires this. */
    seasonId: '1b041b7b-3dde-11f1-8b34-cf648fa5ccb5',
    pinnedFixtureIds: ['3c4c6df2-9cf9-11f1-beab-e764f22a2faf'],
    tabCategory: 'BASKETBALL',
    tabCompetitionSlug: 'nbl',
    tabSha256Hash: TAB_EVENTS_HASH,
  },
  {
    id: League.NZNBL,
    name: 'Sal’s NZ NBL',
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ9hWoF2AqWQQUbGHlzsIxelukh7NcsP0cGKg&s',
    country: 'New Zealand',
    active: true,
    provider: 'genius',
    competitionId: '47827',
    sport: 'basketball',
  },
  {
    id: League.WBC,
    name: 'G.J. Gardner Homes Tauihi Basketball Aotearoa',
    icon: 'data:image/webp;base64,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',
    country: 'New Zealand',
    active: true,
    knowledgeBase: true,
    knowledgeId: 'tauihi',
    // No Tauihi knowledge writer or cron exists yet — LiveScoreUtils only
    // writes for NPC and NRL — so postGameKbWrite and cronSync are deliberately
    // left off rather than promising something that does not happen.
    // `wbc` is this league's own id, so it is not repeated here — every name
    // in `[id, ...aliases]` has to be unique across the whole registry.
    aliases: ['tauihi'],
    articleTags: ['tauihi', 'basketball', 'new zealand'],
    provider: 'genius',
    /**
     * 2026 season. Update this every season — it is the only field separating
     * Tauihi from the Sky Broadband Rapid League, which shares this league's
     * team ids, half its playing rosters and an adjacent block of match ids.
     * Previous seasons: 41777 (2025), 38467 (2024), 35375 (2023), 33221 (2022).
     */
    competitionId: '49434',
    sport: 'basketball',
    dataSource: 'dcd',
  },
  // {
  //   id: League.EPL,
  //   name: 'England Premier League',
  //   icon: 'https://img.sofascore.com/api/v1/unique-tournament/17/image/dark',
  //   country: 'England',
  //   active: true,
  //   provider: 'sofascore',
  //   uniqueTournamentId: '17',
  //   seasonId: '76986',
  //   sport: 'football',
  //   apiVersion: 'v1',
  //   eventsPathGroup: 'england',
  //   eventsPathLeague: 'premier-league',
  // },
  {
    id: League.EPL,
    name: 'England Premier League',
    icon: 'https://img.sofascore.com/api/v1/unique-tournament/17/image/dark',
    country: 'England',
    active: true,
    // Knowledge files exist, but no writer keeps them current — so no
    // postGameKbWrite and no cronSync.
    knowledgeBase: true,
    // Club names are deliberately absent: an article about Arsenal's opponent
    // is still an article about this fixture, and listing twenty clubs would
    // only mean the list goes stale on promotion and relegation.
    articleTags: ['premier league', 'epl', 'football', 'soccer'],
    provider: 'tabnz',
    category: 'SOCCER',
    regionSlug: 'uk-ireland',
    competitionSlug: 'premier-league',
    sha256Hash: TAB_EVENTS_HASH,
    eventSha256Hash: TAB_EVENT_SCREEN_HASH,
    sport: 'football',
  },
  //https://www.sofascore.com/tournament/football/australia/a-league-men/136#id:64864
  {
    id: League.ALM,
    name: 'A-League Men',
    icon: 'https://img.sofascore.com/api/v1/unique-tournament/136/image/dark',
    country: 'Australia',
    active: true,
    provider: 'sofascore',
    uniqueTournamentId: '136',
    // 98511 is A-League 26/27 (82603 was 25/26, 64864 was 24/25). It changes each
    // season; read it from /unique-tournament/136/seasons.
    seasonId: '98511',
    sport: 'football',
    apiVersion: 'v1',
    eventsPathGroup: 'australia',
    eventsPathLeague: 'a-league-men',
    // Auckland FC's squad and season files are written by
    // knowledge/AucklandFcKnowledgeWriter.ts. Tags are specific to the league
    // on purpose: the generic 'football' and 'soccer' tags would let EPL
    // stories through to A-League competitions and the reverse.
    knowledgeBase: true,
    cronSync: true,
    articleTags: ['auckland fc', 'a-league', 'a-league men', 'isuzu ute a-league'],
  },
  {
    id: League.RUIFG,
    name: 'Rugby Union International Friendly Games',
    icon: 'https://img.sofascore.com/api/v1/unique-tournament/876/image/dark',
    country: 'International',
    active: true,
    // Competitions, team records and the stat registry all say `intl`.
    aliases: ['intl'],
    postGameKbWrite: true,
    cronSync: true,
    knowledgeBase: true,
    articleTags: [
      'all blacks',
      'springboks',
      'test match',
      'rugby championship',
      'six nations',
      'internationals',
    ],
    provider: 'rugbypass',
    // RugbyPass aggregates all international test fixtures under this slug.
    competitionSlug: 'internationals',
    sport: 'rugby-union',
    tabCategory: 'RUGBY_UNION',
    // TAB renders the apostrophe as "-s-" in slugs, hence the difference from
    // the human-readable competition name.
    tabCompetitionSlug: 'rugby-s-greatest-rivalry',
    // Persisted-query hashes for the SportingCompetitionScreen and
    // SportingEventScreen GraphQL operations. They identify the query, not the
    // competition, so they carry across competitions unchanged.
    tabSha256Hash: TAB_EVENTS_HASH,
    tabEventSha256Hash: TAB_EVENT_SCREEN_HASH,
  },
  {
    id: League.TST,
    name: 'Test League',
    icon: 'https://as1.ftcdn.net/v2/jpg/00/62/78/62/1000_F_62786254_cxVz7e28OMBn63qGzDFEBqHv7e1o2HgU.jpg',
    country: 'New Zealand',
    active: true,
    provider: 'genius',
    competitionId: '622',
    sport: 'basketball',
  },
];
